import html2pdf from "html2pdf.js";
import { useCallback, useRef, useState } from "react";
import { Toast } from "../../../../components/Toast";
import {
  FILE_EXTENSIONS,
  generateFileName,
} from "../../../../hooks/useFileNaming";
import {
  createPdfConfig,
  createPdfContainer,
  measureContentForPdf,
} from "../utils/commonWidgetUtils";

/**
 * Custom hook to manage PDF export functionality for dashboard widgets
 * Collects export functions from widgets and provides a combined export capability
 */
export const useWidgetPdfExport = () => {
  const widgetExportRefs = useRef({});
  const [exportCount, setExportCount] = useState(0);

  // Register a widget's export function
  const registerWidgetExport = useCallback((widgetId, exportFunction) => {
    if (exportFunction) {
      widgetExportRefs.current[widgetId] = exportFunction;
    } else {
      delete widgetExportRefs.current[widgetId];
    }

    const newCount = Object.keys(widgetExportRefs.current).length;
    setExportCount(newCount);
  }, []);

  // Unregister a widget's export function
  const unregisterWidgetExport = useCallback((widgetId) => {
    delete widgetExportRefs.current[widgetId];
    setExportCount(Object.keys(widgetExportRefs.current).length);
  }, []);

  // Export all widgets that support PDF export as a combined PDF
  const exportAllWidgetsPdf = useCallback(async () => {
    const exportFunctions = Object.entries(widgetExportRefs.current);

    if (exportFunctions.length === 0) {
      Toast.error("No widgets available for PDF export");
      return;
    }

    try {
      // Create main PDF container
      const entirePageElement = await createPdfContainer();

      // Process each widget export
      for (const [widgetId, exportFunction] of exportFunctions) {
        try {
          // Get the widget content for export
          const widgetContent = await exportFunction(true); // Pass true to indicate combined export

          if (widgetContent) {
            // Add spacing between widgets
            const spacer = document.createElement("div");
            spacer.style.cssText = "height: 20px; width: 100%;";
            entirePageElement.appendChild(spacer);

            entirePageElement.appendChild(widgetContent);
          }
        } catch (error) {
          // Continue with other widgets
        }
      }

      // Measure content dimensions
      const { contentWidth, contentHeight } = await measureContentForPdf(
        entirePageElement
      );

      // Generate combined PDF
      const pdfConfig = createPdfConfig(
        generateFileName("Breeze_Dashboard_Report", null, FILE_EXTENSIONS.PDF),
        contentWidth,
        contentHeight
      );

      await html2pdf().set(pdfConfig).from(entirePageElement).save();

      Toast.success("Dashboard PDF exported successfully");
    } catch (error) {
      Toast.error("Failed to export dashboard PDF. Please contact support.");
    }
  }, []);

  return {
    registerWidgetExport,
    unregisterWidgetExport,
    exportAllWidgetsPdf,
    exportCount,
  };
};
